import { useEffect, useState } from "react";
import type { TechProduct } from "@/mocks/techData";

export default function ProductDetail({
  product,
  onClose,
}: {
  product: TechProduct;
  onClose: () => void;
}) {
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  if (!product) return null;

  const enquire = () => {
    onClose();
    setTimeout(() => document.querySelector("#visit")?.scrollIntoView({ behavior: "smooth" }), 60);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground-950/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-background-50"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-foreground-950/80 text-background-50 transition-colors hover:bg-foreground-950"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="grid md:grid-cols-2">
          {/* Gallery */}
          <div className="relative bg-foreground-950">
            <div className="aspect-[4/5] md:h-full md:min-h-[560px]">
              <img
                key={activeImg}
                src={product.images[activeImg]}
                alt={product.name}
                className="h-full w-full object-cover"
                style={{ animation: "imgIn .5s ease-out" }}
              />
            </div>
            {product.images.length > 1 && (
              <div className="absolute bottom-4 left-4 flex gap-2">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    aria-label={`View image ${i + 1}`}
                    className={`h-16 w-14 cursor-pointer overflow-hidden rounded-md border-2 transition-colors ${
                      activeImg === i ? "border-primary-500" : "border-transparent opacity-70"
                    }`}
                  >
                    <img src={img} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <style>{`@keyframes imgIn{from{opacity:0}to{opacity:1}}`}</style>
          </div>

          {/* Info */}
          <div className="flex flex-col p-6 md:p-8">
            <div className="flex items-center gap-2">
              {product.tag && (
                <span className="rounded-full bg-primary-500 px-3 py-1 font-label text-[10px] text-foreground-950">
                  {product.tag}
                </span>
              )}
              <span className="font-label text-[10px] text-secondary-500">
                {product.category}
              </span>
            </div>

            <h3 className="mt-4 font-heading text-2xl text-foreground-950 md:text-3xl">{product.name}</h3>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">{product.description}</p>

            {/* Availability */}
            <div className="mt-6 flex items-center gap-2 text-sm text-foreground-600">
              <span className="flex h-2 w-2 rounded-full bg-primary-500"></span>
              {product.availability}
            </div>
            <p className="mt-2 text-sm text-foreground-500">
              Contact us for pricing and current deals.
            </p>

            <div className="mt-8 flex flex-col gap-3">
              <button onClick={enquire} className="btn-primary h-16 w-full px-5">
                <i className="ri-chat-1-line"></i> Ask for Price
              </button>
              <a href="tel:07909324302" className="btn-ghost h-16 w-full px-5">
                <i className="ri-phone-line"></i> Call Now
              </a>
            </div>

            <p className="mt-6 text-xs text-secondary-500">
              Available at Fist Limited, 86 Eastdale Road, Liverpool L15 4HW — and at our market stall.
              Call to check current stock.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}